import React, { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const TAB_LABELS = { synthese: "Synthèse", verif_documents: "Vérif documents", finance: "Finance client", demarches: "Démarches",
  installation: "Installation", comptabilite: "Comptabilité", sav: "SAV", documents: "Dossier", desinstallation: "Désinstallation", planning: "Planning" };
const inputCls = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

export function Dialog({ title, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-norea-border w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="px-5 py-3 border-b border-norea-border flex items-center justify-between"><h3 className="font-heading text-base font-medium">{title}</h3>
          <button onClick={onClose} className="p-1" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button></div>
        <div className="p-5 space-y-3">{children}</div>
        <div className="px-5 py-3 border-t border-norea-border flex justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}

export function DossierSearch({ value, onChange }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/dossiers", { params: { search: q || undefined, limit: 8 } }).then((r) => setItems(r.data.items || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const name = (d) => [d.extraction?.prenom, d.extraction?.nom].filter(Boolean).join(" ") || d.id;
  return (
    <div className="space-y-1">
      <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-norea-textMuted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom du client, ville, n° de BDC…" className={`${inputCls} pl-7`} data-testid="attach-search" /></div>
      <div className="max-h-48 overflow-y-auto border border-norea-border rounded divide-y divide-norea-border" data-testid="attach-results">
        {items.map((d) => (
          <button key={d.id} onClick={() => onChange(d.id)} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-norea-surfaceHover ${value === d.id ? "bg-norea-primary/10 text-norea-primary font-medium" : ""}`} data-testid={`attach-result-${d.id}`}>
            {name(d)} · {d.extraction?.ville || "—"} · {d.produit || d.extraction?.produit || ""} {d.extraction?.numero_bdc ? `· ${d.extraction.numero_bdc}` : ""}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AttachDialog({ mail, onClose, onDone }) {
  const [dossierId, setDossierId] = useState("");
  const [tab, setTab] = useState("");
  const [team, setTeam] = useState(null);
  const [addTo, setAddTo] = useState("");
  const [busy, setBusy] = useState(false);
  const unknown = !mail.routing?.sender;
  useEffect(() => {
    if (unknown && dossierId) api.get(`/inbox/${mail.id}/team`, { params: { dossier_id: dossierId } }).then((r) => setTeam(r.data.partners)).catch(() => setTeam([]));
  }, [dossierId]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inbox/${mail.id}/attach`, { dossier_id: dossierId, tab: tab || null, add_address_to: addTo || null });
      toast.success(`Email rattaché${data.learned_refs ? ` — ${data.learned_refs} référence(s) retenue(s)` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rattachement impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Rattacher l'email à un dossier" onClose={onClose} testid="attach-dialog"
      footer={<><button onClick={onClose} className="btn-secondary text-sm">Annuler</button>
        <button onClick={go} disabled={!dossierId || busy} className="btn-primary text-sm" data-testid="attach-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Rattacher</button></>}>
      <div className="text-xs text-norea-textMuted">De <strong className="text-norea-text">{mail.from_name || mail.from_email}</strong> — {mail.subject}</div>
      <DossierSearch value={dossierId} onChange={setDossierId} />
      <label className="block text-xs text-norea-textMuted space-y-1"><span>Onglet</span>
        <select value={tab} onChange={(e) => setTab(e.target.value)} className={inputCls} data-testid="attach-tab">
          <option value="">Automatique ({TAB_LABELS[mail.tab] || mail.tab})</option>
          {Object.entries(TAB_LABELS).filter(([k]) => k !== "documents").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      {unknown && dossierId && (
        <label className="block text-xs text-norea-textMuted space-y-1"><span>Adresse inconnue ({mail.from_email}) — l'ajouter à la fiche d'un partenaire ?</span>
          <select value={addTo} onChange={(e) => setAddTo(e.target.value)} className={inputCls} data-testid="attach-add-address">
            <option value="">Non, ne pas l'ajouter</option>
            {(team || []).map((p) => <option key={p.key} value={p.key}>{p.team ? "★ " : ""}{p.label}</option>)}
          </select>
        </label>
      )}
      <p className="text-[11px] text-norea-textMuted">L'IA retient les références citées (n° de dossier, de facture…) pour les prochains emails de ce partenaire, et rattache la suite de la conversation au même dossier.</p>
    </Dialog>
  );
}

export function SimulateDialog({ onClose, onDone }) {
  const [f, setF] = useState({ from_email: "", from_name: "", subject: "", body: "", thread_id: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = async (e) => {
    const list = await Promise.all([...e.target.files].map((file) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ filename: file.name, mime: file.type || "application/octet-stream", content_b64: String(r.result).split(",")[1] || "" });
      r.readAsDataURL(file);
    })));
    setFiles(list);
  };
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inbox/simulate", { ...f, thread_id: f.thread_id || null, attachments: files });
      toast.success(`Email simulé — ${({ attached: "rattaché", proposed: "proposition", unmatched: "à rattacher", ignored: "ignoré" })[data.state]}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Simuler un email reçu" onClose={onClose} testid="simulate-dialog"
      footer={<><button onClick={onClose} className="btn-secondary text-sm">Annuler</button>
        <button onClick={go} disabled={!f.from_email || busy} className="btn-primary text-sm" data-testid="simulate-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Injecter</button></>}>
      <p className="text-[11px] text-norea-textMuted">Aucune boîte réelle n'est utilisée et rien n'est envoyé : l'email passe seulement dans le circuit de lecture IA.</p>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.from_email} onChange={set("from_email")} placeholder="Expéditeur (email)" className={inputCls} data-testid="simulate-from-email" />
        <input value={f.from_name} onChange={set("from_name")} placeholder="Nom affiché" className={inputCls} data-testid="simulate-from-name" />
      </div>
      <input value={f.subject} onChange={set("subject")} placeholder="Objet" className={inputCls} data-testid="simulate-subject" />
      <textarea value={f.body} onChange={set("body")} rows={6} placeholder="Corps du message" className={inputCls} data-testid="simulate-body" />
      <div className="grid grid-cols-2 gap-2 items-center">
        <input value={f.thread_id} onChange={set("thread_id")} placeholder="Conversation (facultatif, ex. thr-1)" className={inputCls} data-testid="simulate-thread" />
        <input type="file" multiple onChange={pick} className="text-xs" data-testid="simulate-files" />
      </div>
    </Dialog>
  );
}
